import Location from "../../hooks/Location"
import { useFirestoreGeneral, useFirestore, useFirestoreGeneralArrayContainsOrderBy } from '../../firebase/useFirestore';
import { useEffect, useMemo, useState, useContext } from 'react';
import plusButton from '../../images/icons/plus-icon.png'
import Tooltip from '../../components/common/Tooltip';
import { db } from '../../firebase/config';
import { timestamp } from '../../firebase/config';
import { v4 as uuid } from 'uuid';
import { client } from "../../hooks/Client"
import { useHistory } from "react-router-dom";
import firebase from '../../firebase/compat'
import { Auth } from "../../StateManagment/Auth";
import notDeleted from "../../helpers/notDeleted";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import SavedNotice from "../../components/common/SavedNotice"
import { DeccaContext } from "../../StateManagment/DeccaContext.js";
import PageHeader from "../../components/layout/PageHeader.jsx";
import PairedToQuestionnaireMeta from '../../components/Indicators/PairedToQuestionnaireMeta';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Modal from '../../components/common/Modal';
import CommonModal from '../../components/common/Modal';
import QuestionnaireField from '../../components/Questionnaire/QuestionnaireField';
import MSIQuestionnaireField from "../../components/Questionnaire/MSIQuestionnaireField";
import DragIconTableData from "../../components/common/DragIconTableData"
import DeleteIndicator from "../../components/Indicators/DeleteIndicator";
import EffectMeta from "../../components/effects/EffectMeta";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import clickableRowProps from '../../helpers/a11y/clickableRowProps';
import scaleStartLabel from '../../helpers/questionnaire/scaleStartLabel';

/**
 * Indicator (vragen) management for an effect. Works as the dedicated
 * /addindicators route AND embedded inline (e.g. in the TOC navigator's effect
 * editor) when given an `effectId` prop + `showHeader=false`.
 *
 * @param {object} [props]
 * @param {string} [props.effectId] - effect ID; falls back to the URL segment for the route
 * @param {boolean} [props.showHeader] - render the page chrome (PageHeader/SavedNotice). Default true.
 */
const AddIndicators = ({ effectId: propEffectId, showHeader = true } = {}) => {
  // Context
  const [auth] = useContext(Auth)
  const [saved, setSaved] = useContext(SavedIcon)
  const { setSelectedEffect } = useContext(DeccaContext);

  // State
  const [dragArray, setDragArray] = useState([]);
  const [showAddContainer, setShowAddContainer] = useState(false)
  const [effectTitle, setEffectTitle] = useState('')
  const [effectDocid, setEffectDocid] = useState('')
  const [editPairing, setEditPairing] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [msiModalOpen, setMSIModalOpen] = useState(false)

  // Store ONLY the selected field ID; derive the object from `fields`
  const [editIndicatorId, setEditIndicatorId] = useState(null)

  // Hooks
  const effectId = propEffectId || Location()[3]
  const history = useHistory()


  // Firestore
  const fields = useFirestoreGeneralArrayContainsOrderBy('QuestionnaireFields', 'EffectId', effectId ? effectId : '', 'IndicatorPosition', 'asc');
  const effects = useFirestoreGeneral('OutputEffects', 'ID', effectId ? effectId : '')
  const questionaires = useFirestore('Questionnaires')

  // Selected effect in context
  useEffect(() => {
    setSelectedEffect({ name: effectTitle, id: effectId })
  }, [effectTitle, effectId, setSelectedEffect])

  // Keep local drag array mirrored with snapshot
  useEffect(() => {
    setDragArray(fields || []);
  }, [fields]);

  // Derive the selected field fresh from `fields`
  const selectedField = useMemo(
    () => (fields || []).find(f => f.ID === editIndicatorId) || null,
    [fields, editIndicatorId]
  );
  const selectedFieldIndex = useMemo(
    () => (fields || []).findIndex(f => f.ID === editIndicatorId),
    [fields, editIndicatorId]
  );

  // DRAG & DROP: reorder + batch write
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);
    setDragArray(items);

    const batch = db.batch();
    items.forEach((it, idx) => {
      const ref = db.collection('QuestionnaireFields').doc(it.docid);
      batch.update(ref, { IndicatorPosition: idx + 1 });
    });
    await batch.commit();
    setSaved('flex');
  };

  // Get title/docid of effect
  useEffect(() => {
    effects && effects.forEach(effect => {
      setEffectTitle(effect.Effect)
      setEffectDocid(effect.docid)
    })
  }, [effects])

  // Add field
  const addQuestion = () => {
    db.collection('QuestionnaireFields').doc().set({
      Compagny: client,
      CompagnyID: client,
      ID: uuid(),
      Timestamp: timestamp,
      QuestionnaireID: '',
      Type: 'scale',
      Question: 'Nieuwe vraag',
      Explainer: '',
      ReachStart: 0,
      ReachStartLable: 'Helemaal niet',
      ReachEnd: 5,
      ReachEndLabel: 'Helemaal wel',
      Multiple: [],
      SectionID: '',
      EffectId: firebase.firestore.FieldValue.arrayUnion(effectId),
      Indicator: 'true',
      LiveReportHidden: false,
      Standard: 'false',
      Position: (fields?.length || 0) + 1,
      IndicatorPosition: (fields?.length || 0) + 1,
    })
  }

  const titleHandler = (e) => {
    const docid = e.target.dataset.docid
    db.collection('QuestionnaireFields').doc(docid).update({ Question: e.target.value })
  }

  const indicatorType = (type) => {
    switch (type) {
      case 'scale': return 'Schaalvraag';
      case 'multiple-one': return 'Meerkeuze vraag met één mogelijk antwoord';
      case 'multiple-multiple': return 'Meerkeuze vraag met meerdere mogelijke antwoorden';
      case 'paragraph': return 'Open vraag';
      case 'number': return 'Numerieke vraag';
      case 'matrix-one': return 'Matrix vraag met één mogelijk antwoord';
      case 'matrix-multiple': return 'Matrix vraag met meerdere mogelijke antwoorden';
      default: return type;
    }
  }

  // Copy question
  const duplicateHandler = async (e) => {
    const questionaireId = e.currentTarget.dataset.questionaireid
    const type = e.currentTarget.dataset.type
    const question = e.currentTarget.dataset.question
    const explainer = e.currentTarget.dataset.explainer
    const reachStart = e.currentTarget.dataset.reachstart
    const reachStartLable = e.currentTarget.dataset.reachstartlable
    const reachEnd = e.currentTarget.dataset.reachend
    const reachEndLabel = e.currentTarget.dataset.reachendlabel
    const multiple = e.currentTarget.dataset.multiple
    const sectionID = e.currentTarget.dataset.sectionid
    const id = e.currentTarget.dataset.id
    const MSIID = e.currentTarget.dataset.msiid
    const standard = e.currentTarget.dataset.standard
    const matrixBipolar = e.currentTarget.dataset.matrixbipolar === 'true'
    const effId = e.currentTarget.dataset.effectid

    const fieldID = uuid()

    await db.collection('QuestionnaireFields').doc().set({
      Compagny: client,
      CompagnyID: client,
      ID: fieldID,
      Timestamp: timestamp,
      QuestionnaireID: questionaireId,
      Type: type,
      EffectId: firebase.firestore.FieldValue.arrayUnion(effId),
      Indicator: 'true',
      Question: ('Kopie van ' + question),
      Explainer: explainer,
      ReachStart: Number(reachStart),
      ReachStartLable: reachStartLable,
      ReachEnd: Number(reachEnd),
      ReachEndLabel: reachEndLabel,
      Position: (fields?.length || 0) + 1,
      Multiple: multiple,
      SectionID: sectionID,
      LiveReportHidden: false,
      MSIId: MSIID || '',
      Standard: standard,
      MatrixBipolar: matrixBipolar
    })

    await duplicateOptions(id, fieldID, question)
    await duplicateMatrixRows(id, fieldID, question)
    await duplicateMatrixColumns(id, fieldID, question)
  }

  const duplicateOptions = async (srcFieldId, newFieldID, question) => {
    const snap = await db.collection('MultipleQuestionOptions')
      .where('CompagnyID', '==', client)
      .where('Field', '==', srcFieldId)
      .get()
    const batch = db.batch()
    snap.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
      const ref = db.collection('MultipleQuestionOptions').doc()
      batch.set(ref, {
        Option: doc.data().Option,
        Question: question,
        Field: newFieldID,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Position: doc.data().Position,
        ID: uuid(),
      })
    })
    await batch.commit()
  }

  const duplicateMatrixRows = async (srcFieldId, newFieldID, question) => {
    const snap = await db.collection('MatrixQuestionRows')
      .where('Compagny', '==', client)
      .where('Field', '==', srcFieldId)
      .get()
    const batch = db.batch()
    snap.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
      const ref = db.collection('MatrixQuestionRows').doc()
      batch.set(ref, {
        Title: doc.data().Title,
        TitleRight: doc.data().TitleRight || '',
        Question: question,
        Field: newFieldID,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Position: doc.data().Position,
        ID: uuid(),
      })
    })
    await batch.commit()
  }

  const duplicateMatrixColumns = async (srcFieldId, newFieldID, question) => {
    const snap = await db.collection('MatrixQuestionColumns')
      .where('Compagny', '==', client)
      .where('Field', '==', srcFieldId)
      .get()
    const batch = db.batch()
    snap.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
      const ref = db.collection('MatrixQuestionColumns').doc()
      batch.set(ref, {
        Title: doc.data().Title,
        Question: question,
        Field: newFieldID,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Position: doc.data().Position,
        ID: uuid(),
      })
    })
    await batch.commit()
  }

  // Pairing handler
  const pairingHandler = async (e) => {
    const docid = e.target.dataset.docid
    const questionnaireId = e.target.value
    await db.collection('QuestionnaireFields').doc(docid).update({ QuestionnaireID: questionnaireId })
    setEditPairing(false)
  }

  // Question title change in modal
  const questionTitleHandler = (e) => {
    const docid = e.target.dataset.docid;
    const question = e.target.value;
    db.collection('QuestionnaireFields').doc(docid).update({ Question: question })
  }

  const body = (
    <>
      <ReadOnlyGuard edit="measurement.edit">
        {(canEdit) => (
          <>
        <Table
          columns={[
            { key: 'drag', label: '', searchable: false },
            { key: 'question', label: 'VRAAG' },
            { key: 'edit', label: 'AANPASSEN', searchable: false },
            { key: 'copy', label: 'KOPIEREN', searchable: false },
            { key: 'pair', label: 'KOPPELEN', searchable: false },
            { key: 'delete', label: 'VERWIJDEREN', searchable: false },
          ]}
          items={dragArray || []}
          draggable
          droppableId="stakeholders"
          onDragEnd={handleDragEnd}
          getItemKey={(field) => field.ID}
          searchAccessor={(field) => field?.Question || ''}
          searchPlaceholder="Zoeken op vraag"
          emptyMessage="Geen vragen gevonden."
          addRow={canEdit ? {
            toolTipContent: 'Vraag toevoegen',
            onClick: () => setShowAddContainer(!showAddContainer),
          } : undefined}
          renderRow={(field, index) => (
            <>
              <DragIconTableData effect={null} output={null} question={field} />
              <TableCell>
                {field.Standard === 'true' ? (
                  <div id='meetstandaard-effect-meta-container'>
                    <Tooltip content={'Gestandaardiseerde indicatoren kunnen niet worden aangepast'} width='80%' right='-120px' top='-15px'>
                      <p className='table-p tooltip-p-normal' id='add-inicator-meetstandaard-effect-title-container'>
                        <EffectMeta item={field.EffectId[0]}/>
                      </p>
                    </Tooltip>
                    <a href="https://meetstandaard.nl" target="_blank" rel="noopener noreferrer" className="question-standardized-sufix">(meetstandaard.nl)</a>
                  </div>
                ) : (
                  <Tooltip content={field.Question} width='100%' top='-40px'>
                    <input
                      placeholder='Schrijf hier de vraag'
                      defaultValue={field.Question ?? ''}
                      data-docid={field.docid}
                      onChange={titleHandler}
                    />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <Tooltip content={'Indicator aanpassen'} width='80%' top='-60px'>
                  <div id='edit-indicator-button-type-container'>
                    {field.Standard === 'true' ? (
                      <>
                        <SearchOutlinedIcon onClick={() => { setMSIModalOpen(true); setEditIndicatorId(field.ID); }} />
                        <p>({indicatorType(field.Type)})</p>
                      </>
                    ) : (
                      <>
                        {canEdit && <EditOutlinedIcon onClick={() => { setEditIndicatorId(field.ID); setModalOpen(true); }} />}
                        <p>({indicatorType(field.Type)})</p>
                      </>
                    )}
                  </div>
                </Tooltip>
              </TableCell>
              <TableCell>
                {canEdit && (
                  <Tooltip content={'Vraag kopiëren'} width='100%' top='-60px'>
                    <ContentCopyOutlinedIcon
                      id='remove-field-from-section-icon'
                      className='table-delete-icon'
                      data-id={field.ID}
                      data-index={index}
                      data-questionaireid={field.QuestionnaireID}
                      data-type={field.Type}
                      data-question={field.Question}
                      data-explainer={field.Explainer}
                      data-reachstart={field.ReachStart}
                      data-reachstartlable={scaleStartLabel(field)}
                      data-reachend={field.ReachEnd}
                      data-reachendlabel={field.ReachEndLabel}
                      data-position={field.Position}
                      data-multiple={field.Multiple}
                      data-sectionid={field.SectionID}
                      data-color={field.Color}
                      data-title={field.Title}
                      data-msiid={field.MSIId}
                      data-standard={field.Standard}
                      data-matrixbipolar={field.MatrixBipolar}
                      data-effectid={field.EffectId}
                      onClick={duplicateHandler}
                    />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                {editPairing === field.ID ? (
                  <div id="add-inidicator-paired-to-questionaire-container">
                    <select onChange={pairingHandler} data-docid={field.docid} value={field.QuestionnaireID ?? ''}>
                      <option value="">-- Koppel aan vragenlijst --</option>
                      {questionaires && questionaires.map((item, idx) => (
                        <option key={idx} value={item.ID}>{item.Title}</option>
                      ))}
                    </select>
                    {canEdit && (
                      <Tooltip content={'Koppeling aanpassen'} width='20px' top='-60px'>
                        <EditOutlinedIcon onClick={() => setEditPairing(editPairing === field.ID ? null : field.ID)}/>
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <div id='add-inidicator-paired-to-questionaire-container'>
                    <PairedToQuestionnaireMeta questionnaireId={field.QuestionnaireID} />
                    <div id='add-inidicator-paired-to-questionaire-options-container'>
                      {field.QuestionnaireID !== '' &&
                        <Tooltip content={'Bekijk vragenlijst'} width='100%' top='-60px'>
                          <SearchOutlinedIcon onClick={() => history.push(`/${client}/questionairebuilder/${field.QuestionnaireID}`)}/>  
                        </Tooltip>
                      }
                    </div>
                    {canEdit && (
                      <Tooltip content={'Koppeling aanpassen'} width='20px' top='-60px'>
                        <EditOutlinedIcon onClick={() => setEditPairing(editPairing === field.ID ? null : field.ID)}/>
                      </Tooltip>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {canEdit && <DeleteIndicator field={field} />}
              </TableCell>
            </>
          )}
        />
          
          <CommonModal
            modalOpen={showAddContainer}
            setModalOpen={setShowAddContainer}
            pageHeader={'Vraag toevoegen'}
            hideConfirm={true}
            content={
              <>
                <div className="add-options-container-item-container" {...clickableRowProps(() => { setShowAddContainer(false); addQuestion(); })}>
                  <img src={plusButton} alt="" />
                  <p>Nieuwe vraag toevoegen</p>
                </div>
                <div className="add-options-container-item-container" {...clickableRowProps(() => { setShowAddContainer(false); history.push(`/${client}/selectkpis/${effectId}`); })}>
                  <img src={plusButton} alt="" />
                  <p>Bestaande vragen toevoegen</p>
                </div>
              </>
            }
          />
          </>
        )}
      </ReadOnlyGuard>
    </>
  )

  return (
    <>
      {showHeader ? (
        <div className="main">
          <div className="main-container">
            <PageHeader
              heading={'Vragen toevoegen'}
              subHeading={`${effectTitle}`}
              description={`In deze stap ga je vragen formuleren waarmee je het effect  ${effectTitle} kunt onderbouwen.`}
              AI={false}
            />
            {body}
            <div className="impactstrategy-saved-and-nextstep-container">
              <SavedNotice />
            </div>
          </div>
        </div>
      ) : body}

      {/* Edit modal */}
      <Modal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        pageHeader="Vraag aanpassen"
        confirm={() => setModalOpen(false)}
        content={selectedField ? (
          <div>
            {/* De vraagtekst stond vroeger IN de kop van de modal. De gedeelde
                kop is een <h2> met een sluitknop, dus het invoerveld staat nu in
                de inhoud, met een eigen label erboven. */}
            <div id='edit-indicator-modal-title'>
              <p><b>Vraag</b></p>
              <input
                type="text"
                value={selectedField.Question ?? ''}
                data-docid={selectedField.docid}
                onChange={questionTitleHandler}
              />
            </div>
            <QuestionnaireField field={selectedField} index={selectedFieldIndex}/>
          </div>
        ) : null}
      />

      {/* MSI preview modal */}
      <Modal
        modalOpen={msiModalOpen}
        setModalOpen={setMSIModalOpen}
        pageHeader={selectedField?.Question || 'Meetstandaardvraag'}
        hideConfirm
        cancelLabel="Klaar"
        content={selectedField ? <MSIQuestionnaireField field={selectedField} /> : null}
      />
    </>
  )
}

export default AddIndicators
